import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Nav from "@/components/site/Nav";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "fde_admin_passcode";
const STATUSES = ["pending", "accepted", "rejected"];

function statusStyle(s) {
  switch (s) {
    case "accepted": return "bg-emerald-50 text-emerald-800 border-emerald-200";
    case "rejected": return "bg-rose-50 text-rose-800 border-rose-200";
    default: return "bg-amber-50 text-amber-800 border-amber-200";
  }
}

const BLANK_INSTRUCTOR = {
  name: "", title: "Lead Instructor", headline: "", bio: "",
  photo_url: "", website: "", linkedin: "", github: "",
  email: "", phone: "", location: "",
  skills: "", certifications: "",
  years_experience: "", projects_count: "",
  education: [], experience: [], is_active: true,
};
const BLANK_EDU = { degree: "", institution: "", year: "", grade: "" };
const BLANK_EXP = { company: "", role: "", type: "CONTRACT", period: "", location: "", highlights: "" };

function toArray(str) {
  return str.split(",").map((s) => s.trim()).filter(Boolean);
}
function fromArray(arr) {
  return Array.isArray(arr) ? arr.join(", ") : arr || "";
}

function DetailRow({ label, value }) {
  return (
    <div className="flex gap-6 border-b border-slate-100 pb-3">
      <p className="w-32 shrink-0 font-mono uppercase text-[10px] tracking-[0.24em] text-slate-500 pt-1">{label}</p>
      <p className="text-slate-800 font-sans break-all">{value}</p>
    </div>
  );
}

function FieldLabel({ children }) {
  return <Label className="font-mono uppercase text-[10px] tracking-[0.2em] text-slate-500">{children}</Label>;
}

function FormInput({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div className="space-y-1">
      <FieldLabel>{label}</FieldLabel>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-none border-slate-300 focus-visible:ring-slate-900 focus-visible:ring-1 h-10 text-sm" />
    </div>
  );
}

function FormTextarea({ label, value, onChange, placeholder, rows = 4 }) {
  return (
    <div className="space-y-1">
      <FieldLabel>{label}</FieldLabel>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} rows={rows}
        className="rounded-none border-slate-300 focus-visible:ring-slate-900 focus-visible:ring-1 text-sm resize-none" />
    </div>
  );
}

function EducationEditor({ items, onChange }) {
  const update = (i, field, val) => onChange(items.map((it, idx) => idx === i ? { ...it, [field]: val } : it));
  const add = () => onChange([...items, { ...BLANK_EDU }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <FieldLabel>Education</FieldLabel>
        <button type="button" onClick={add}
          className="text-xs font-mono uppercase tracking-widest border border-slate-300 hover:border-slate-900 px-3 py-1">+ Add</button>
      </div>
      {items.map((edu, i) => (
        <div key={i} className="border border-slate-200 p-4 space-y-3 bg-slate-50">
          <div className="grid grid-cols-2 gap-3">
            <FormInput label="Degree" value={edu.degree} onChange={(v) => update(i, "degree", v)} />
            <FormInput label="Institution" value={edu.institution} onChange={(v) => update(i, "institution", v)} />
            <FormInput label="Year" value={edu.year} onChange={(v) => update(i, "year", v)} />
            <FormInput label="Grade / GPA" value={edu.grade || ""} onChange={(v) => update(i, "grade", v)} />
          </div>
          <button type="button" onClick={() => remove(i)}
            className="text-xs font-mono uppercase tracking-widest text-rose-600 hover:text-rose-800">Remove</button>
        </div>
      ))}
    </div>
  );
}

function ExperienceEditor({ items, onChange }) {
  const update = (i, field, val) => onChange(items.map((it, idx) => idx === i ? { ...it, [field]: val } : it));
  const add = () => onChange([...items, { ...BLANK_EXP }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <FieldLabel>Experience</FieldLabel>
        <button type="button" onClick={add}
          className="text-xs font-mono uppercase tracking-widest border border-slate-300 hover:border-slate-900 px-3 py-1">+ Add</button>
      </div>
      {items.map((exp, i) => (
        <div key={i} className="border border-slate-200 p-4 space-y-3 bg-slate-50">
          <div className="grid grid-cols-2 gap-3">
            <FormInput label="Company / Institute" value={exp.company} onChange={(v) => update(i, "company", v)} />
            <FormInput label="Role" value={exp.role} onChange={(v) => update(i, "role", v)} />
            <FormInput label="Type (CONTRACT / FULL-TIME)" value={exp.type} onChange={(v) => update(i, "type", v)} />
            <FormInput label="Period" value={exp.period} onChange={(v) => update(i, "period", v)} placeholder="Jun 2024 – Present" />
            <FormInput label="Location" value={exp.location} onChange={(v) => update(i, "location", v)} />
          </div>
          <FormTextarea label="Highlights (one per line)" value={exp.highlights}
            onChange={(v) => update(i, "highlights", v)} rows={3} placeholder="Led a team of 5..." />
          <button type="button" onClick={() => remove(i)}
            className="text-xs font-mono uppercase tracking-widest text-rose-600 hover:text-rose-800">Remove</button>
        </div>
      ))}
    </div>
  );
}

function InstructorFormDialog({ open, onClose, onSave, initial }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(BLANK_INSTRUCTOR);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          ...initial,
          skills: fromArray(initial.skills),
          certifications: fromArray(initial.certifications),
          education: initial.education || [],
          experience: (initial.experience || []).map((e) => ({
            ...e,
            highlights: Array.isArray(e.highlights) ? e.highlights.join("\n") : (e.highlights || ""),
          })),
        });
      } else {
        setForm(BLANK_INSTRUCTOR);
      }
    }
  }, [open, initial]);

  const set = (field) => (val) => setForm((f) => ({ ...f, [field]: val }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        skills: toArray(form.skills),
        certifications: toArray(form.certifications),
        experience: form.experience.map((exp) => ({
          ...exp,
          highlights: typeof exp.highlights === "string"
            ? exp.highlights.split("\n").map((s) => s.trim()).filter(Boolean)
            : exp.highlights || [],
        })),
      };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await onSave(payload, initial?.id);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <p className="font-mono uppercase text-[10px] tracking-[0.24em] text-slate-500 mb-2">
            {isEdit ? "Edit instructor" : "Add instructor"}
          </p>
          <DialogTitle className="font-serif text-3xl tracking-tight">
            {isEdit ? form.name || "Instructor" : "New Instructor"}
          </DialogTitle>
          <DialogDescription className="font-sans text-slate-500 text-sm">
            All changes are saved to the remote database immediately.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-6 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Full name *" value={form.name} onChange={set("name")} placeholder="Umashankar K H" />
            <FormInput label="Title / Role" value={form.title} onChange={set("title")} placeholder="Lead Instructor" />
          </div>
          <FormInput label="Headline" value={form.headline} onChange={set("headline")} placeholder="Data Scientist · GenAI & Agentic AI Systems" />
          <FormTextarea label="Bio" value={form.bio} onChange={set("bio")} rows={5} placeholder="Write a short professional bio..." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Email" value={form.email || ""} onChange={set("email")} placeholder="email@example.com" />
            <FormInput label="Phone" value={form.phone || ""} onChange={set("phone")} placeholder="+91 9999999999" />
            <FormInput label="Location" value={form.location || ""} onChange={set("location")} placeholder="Bangalore, India" />
            <FormInput label="Website" value={form.website || ""} onChange={set("website")} placeholder="https://www.umashankar.space" />
            <FormInput label="LinkedIn URL" value={form.linkedin || ""} onChange={set("linkedin")} placeholder="https://linkedin.com/in/..." />
            <FormInput label="GitHub URL" value={form.github || ""} onChange={set("github")} placeholder="https://github.com/..." />
          </div>
          <FormInput label="Photo URL" value={form.photo_url || ""} onChange={set("photo_url")} placeholder="https://..." />
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="Years experience" value={form.years_experience || ""} onChange={set("years_experience")} placeholder="4+" />
            <FormInput label="Projects count" value={form.projects_count || ""} onChange={set("projects_count")} placeholder="10+" />
          </div>
          <FormTextarea label="Skills (comma-separated)" value={form.skills} onChange={set("skills")} rows={3}
            placeholder="Python, TensorFlow, LangChain, RAG, MLOps, Azure, GCP..." />
          <FormTextarea label="Certifications (comma-separated)" value={form.certifications} onChange={set("certifications")} rows={2}
            placeholder="Microsoft AI-900 Trainer, Microsoft AI-102 Trainer..." />
          <EducationEditor items={form.education} onChange={set("education")} />
          <ExperienceEditor items={form.experience} onChange={set("experience")} />
          <div className="flex items-center gap-3">
            <input type="checkbox" id="is_active" checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              className="h-4 w-4 accent-slate-900" />
            <label htmlFor="is_active" className="font-mono uppercase text-[10px] tracking-[0.2em] text-slate-600">
              Active (visible on site)
            </label>
          </div>
          <div className="pt-4 border-t border-slate-200 flex gap-3">
            <button type="submit" disabled={saving}
              className="bg-slate-900 text-white px-8 py-3 font-sans tracking-wide hover:bg-slate-800 transition-colors disabled:opacity-50">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add instructor"}
            </button>
            <button type="button" onClick={onClose}
              className="border border-slate-300 hover:border-slate-900 px-6 py-3 font-sans text-sm">Cancel</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InstructorsTab({ passcode }) {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const headers = { "x-admin-passcode": passcode };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/instructors`, { headers });
      setInstructors(res.data);
    } catch {
      toast.error("Failed to load instructors.");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passcode]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (payload, id) => {
    if (id) {
      await axios.put(`${API}/admin/instructors/${id}`, payload, { headers });
      toast.success("Instructor updated.");
    } else {
      await axios.post(`${API}/admin/instructors`, payload, { headers });
      toast.success("Instructor added.");
    }
    await load();
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/instructors/${id}`, { headers });
      toast.success("Instructor removed.");
      setDeleteTarget(null);
      await load();
    } catch {
      toast.error("Failed to delete instructor.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <p className="font-sans text-slate-600 text-sm">
          {instructors.length} / 5 instructors
          {instructors.length >= 5 && (
            <span className="ml-2 text-amber-600 font-mono text-[10px] uppercase tracking-widest">· limit reached</span>
          )}
        </p>
        <div className="flex gap-3">
          <button onClick={load}
            className="border border-slate-300 hover:border-slate-900 px-5 py-2 text-sm font-sans">
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button onClick={() => { setEditing(null); setFormOpen(true); }}
            disabled={instructors.length >= 5}
            className="bg-slate-900 text-white px-6 py-2 text-sm font-sans hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            + Add instructor
          </button>
        </div>
      </div>

      {instructors.length === 0 && !loading ? (
        <div className="bg-white border border-slate-200 p-16 text-center">
          <p className="font-mono uppercase text-[10px] tracking-[0.24em] text-slate-400 mb-4">No instructors yet</p>
          <button onClick={() => { setEditing(null); setFormOpen(true); }}
            className="bg-slate-900 text-white px-6 py-3 text-sm font-sans hover:bg-slate-800 transition-colors">
            Add first instructor
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {instructors.map((inst) => (
            <div key={inst.id} className="bg-white border border-slate-200 p-6 flex gap-6 items-start">
              {inst.photo_url ? (
                <img src={inst.photo_url} alt={inst.name} className="w-16 h-16 object-cover grayscale shrink-0" />
              ) : (
                <div className="w-16 h-16 bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                  <span className="font-serif text-2xl text-slate-400">{inst.name?.charAt(0) || "?"}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-start gap-3 justify-between">
                  <div>
                    <h3 className="font-serif text-xl text-slate-900">{inst.name}</h3>
                    <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-slate-500 mt-1">{inst.title}</p>
                    {inst.headline && <p className="text-slate-600 text-sm mt-1">{inst.headline}</p>}
                  </div>
                  <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-1 ${inst.is_active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                    {inst.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 font-sans">
                  {inst.location && <span>📍 {inst.location}</span>}
                  {inst.email && <span>✉ {inst.email}</span>}
                  {inst.years_experience && <span>⏱ {inst.years_experience} exp</span>}
                  {inst.projects_count && <span>🗂 {inst.projects_count} projects</span>}
                  {inst.website && <a href={inst.website} target="_blank" rel="noopener noreferrer" className="hover:text-slate-800 underline">Website ↗</a>}
                  {inst.linkedin && <a href={inst.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-slate-800 underline">LinkedIn ↗</a>}
                  {inst.github && <a href={inst.github} target="_blank" rel="noopener noreferrer" className="hover:text-slate-800 underline">GitHub ↗</a>}
                </div>
                {inst.skills?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {inst.skills.slice(0, 10).map((s) => (
                      <span key={s} className="text-[11px] border border-slate-200 px-2 py-0.5 text-slate-600 bg-slate-50">{s}</span>
                    ))}
                    {inst.skills.length > 10 && (
                      <span className="text-[11px] text-slate-400 px-2 py-0.5">+{inst.skills.length - 10} more</span>
                    )}
                  </div>
                )}
                {inst.certifications?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {inst.certifications.map((c) => (
                      <span key={c} className="text-[11px] border border-blue-200 px-2 py-0.5 text-blue-700 bg-blue-50">{c}</span>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex gap-3">
                  <button onClick={() => { setEditing(inst); setFormOpen(true); }}
                    className="text-xs font-mono uppercase tracking-widest border border-slate-300 hover:border-slate-900 px-4 py-1.5">Edit</button>
                  <button onClick={() => setDeleteTarget(inst)}
                    className="text-xs font-mono uppercase tracking-widest border border-rose-200 text-rose-600 hover:border-rose-600 px-4 py-1.5">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <InstructorFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSave={handleSave}
        initial={editing}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="rounded-none max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Delete instructor?</DialogTitle>
            <DialogDescription className="font-sans text-slate-600 text-sm">
              This will permanently remove <strong>{deleteTarget?.name}</strong> from the database.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <button onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-rose-600 text-white px-6 py-2.5 text-sm font-sans hover:bg-rose-700 transition-colors">Delete</button>
            <button onClick={() => setDeleteTarget(null)}
              className="border border-slate-300 hover:border-slate-900 px-6 py-2.5 text-sm font-sans">Cancel</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("applications");
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, accepted: 0, rejected: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async (pc) => {
    setLoading(true);
    try {
      const headers = { "x-admin-passcode": pc };
      const [aRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/applications`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setApps(aRes.data);
      setStats(sRes.data);
    } catch {
      toast.error("Failed to load applications.");
      setAuthed(false);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setPasscode(stored);
      setAuthed(true);
      loadData(stored).finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, [loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    try {
      await axios.post(`${API}/admin/login`, { passcode });
      localStorage.setItem(STORAGE_KEY, passcode);
      setAuthed(true);
      toast.success("Signed in.");
      await loadData(passcode);
    } catch {
      toast.error("Invalid passcode.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPasscode("");
    setApps([]);
  };

  const updateStatus = async (app, status) => {
    try {
      await axios.patch(
        `${API}/admin/applications/${app.id}`,
        { status },
        { headers: { "x-admin-passcode": passcode } }
      );
      toast.success(`Marked as ${status}.`);
      await loadData(passcode);
      setSelected((s) => (s ? { ...s, status } : s));
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm("Delete this application? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/applications/${id}`, { headers: { "x-admin-passcode": passcode } });
      toast.success("Application deleted.");
      setSelected((s) => (s?.id === id ? null : s));
      await loadData(passcode);
    } catch {
      toast.error("Failed to delete application.");
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <p className="font-mono uppercase text-xs tracking-[0.24em] text-slate-500">Loading…</p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] text-slate-900" data-testid="admin-login-page">
        <Nav />
        <main className="max-w-md mx-auto px-6 md:px-10 pt-40 pb-32">
          <p className="font-mono uppercase tracking-[0.24em] text-xs text-slate-500 mb-6">— Admin access</p>
          <h1 className="font-serif text-5xl md:text-6xl leading-tight tracking-tight text-slate-900">Enter passcode.</h1>
          <p className="mt-6 text-slate-600 font-sans">Only reviewers with the shared passcode can access the admin console.</p>
          <form onSubmit={handleLogin} className="mt-12 space-y-6">
            <Input data-testid="admin-login-input" type="password" value={passcode}
              onChange={(e) => setPasscode(e.target.value)} placeholder="Admin passcode"
              className="rounded-none border-slate-300 focus-visible:ring-slate-900 focus-visible:ring-1 h-12" autoFocus />
            <button type="submit" data-testid="admin-login-btn"
              className="w-full bg-slate-900 text-white px-8 py-4 font-sans tracking-wide hover:bg-slate-800 transition-colors">
              Sign in
            </button>
          </form>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-900" data-testid="admin-dashboard">
      <Nav />
      <main className="max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-mono uppercase tracking-[0.24em] text-xs text-slate-500 mb-4">— Admin console</p>
            <h1 className="font-serif text-5xl md:text-6xl leading-[1] tracking-tighter text-slate-900">Dashboard.</h1>
          </div>
          <button onClick={handleLogout} data-testid="admin-logout-btn"
            className="text-slate-600 hover:text-slate-900 px-4 py-3 text-sm font-sans self-end">Sign out</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-12 border-b border-slate-200">
          {[{ key: "applications", label: "Applications" }, { key: "instructors", label: "Instructors" }].map(({ key, label }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`px-6 py-3 font-mono uppercase text-xs tracking-[0.2em] border-b-2 transition-colors ${
                tab === key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"
              }`}>{label}</button>
          ))}
        </div>

        {/* Applications tab */}
        {tab === "applications" && (
          <div>
            <div className="flex justify-end mb-8">
              <button onClick={() => loadData(passcode)} data-testid="admin-refresh-btn"
                className="border border-slate-300 hover:border-slate-900 px-6 py-3 text-sm font-sans">
                {loading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16" data-testid="admin-stats">
              {[
                { label: "Total", value: stats.total },
                { label: "Pending", value: stats.pending },
                { label: "Accepted", value: stats.accepted },
                { label: "Rejected", value: stats.rejected },
              ].map((s) => (
                <div key={s.label} className="bg-white border border-slate-200 p-6"
                  data-testid={`admin-stat-${s.label.toLowerCase()}`}>
                  <p className="font-mono uppercase text-[10px] tracking-[0.24em] text-slate-500 mb-3">{s.label}</p>
                  <p className="font-serif text-4xl tracking-tight text-slate-900">{s.value}</p>
                </div>
              ))}
            </div>
            <div className="bg-white border border-slate-200" data-testid="admin-applications-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200 hover:bg-transparent">
                    {["Name", "Email", "Background", "Experience", "Received", "Status", "Actions"].map((h) => (
                      <TableHead key={h} className={`text-[10px] font-mono uppercase tracking-[0.24em] text-slate-500 ${h === "Actions" ? "text-right" : ""}`}>{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apps.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-16 text-slate-500 font-sans">No applications yet.</TableCell>
                    </TableRow>
                  ) : (
                    apps.map((a) => (
                      <TableRow key={a.id} className="border-slate-100 hover:bg-slate-50 cursor-pointer"
                        data-testid={`application-row-${a.id}`} onClick={() => setSelected(a)}>
                        <TableCell className="font-sans font-medium text-slate-900">{a.full_name}</TableCell>
                        <TableCell className="font-sans text-slate-600 text-sm">{a.email}</TableCell>
                        <TableCell className="font-sans text-slate-600 text-sm">{a.background}</TableCell>
                        <TableCell className="font-sans text-slate-600 text-sm">{a.years_experience}</TableCell>
                        <TableCell className="font-sans text-slate-500 text-sm">
                          {new Date(a.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] font-mono uppercase tracking-widest ${statusStyle(a.status)}`}>{a.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="border border-slate-200 hover:border-slate-900 px-3 py-1 text-xs font-mono uppercase tracking-widest"
                                data-testid={`application-actions-${a.id}`}>···</button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-none">
                              {STATUSES.map((s) => (
                                <DropdownMenuItem key={s} onClick={() => updateStatus(a, s)}
                                  className="font-mono uppercase text-xs tracking-widest cursor-pointer"
                                  data-testid={`action-${s}-${a.id}`}>Mark {s}</DropdownMenuItem>
                              ))}
                              <DropdownMenuItem onClick={() => deleteApplication(a.id)}
                                className="font-mono uppercase text-xs tracking-widest cursor-pointer text-rose-600 focus:text-rose-600">
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {tab === "instructors" && <InstructorsTab passcode={passcode} />}
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="rounded-none max-w-2xl bg-white" data-testid="application-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <p className="font-mono uppercase text-[10px] tracking-[0.24em] text-slate-500 mb-3">Application detail</p>
                <DialogTitle className="font-serif text-3xl tracking-tight">{selected.full_name}</DialogTitle>
                <DialogDescription className="font-sans text-slate-600">{selected.email} · {selected.phone}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4 text-sm">
                <DetailRow label="Background" value={selected.background} />
                <DetailRow label="Experience" value={selected.years_experience} />
                {selected.linkedin && <DetailRow label="LinkedIn" value={selected.linkedin} />}
                {selected.resume_link && <DetailRow label="Resume" value={selected.resume_link} />}
                {selected.preferred_cohort && <DetailRow label="Preferred cohort" value={selected.preferred_cohort} />}
                <div>
                  <p className="font-mono uppercase text-[10px] tracking-[0.24em] text-slate-500 mb-2">Motivation</p>
                  <p className="text-slate-800 font-sans leading-relaxed whitespace-pre-line">{selected.motivation}</p>
                </div>
                <div>
                  <p className="font-mono uppercase text-[10px] tracking-[0.24em] text-slate-500 mb-2">Received</p>
                  <p className="text-slate-800 font-sans">{new Date(selected.created_at).toLocaleString("en-IN")}</p>
                </div>
                <div className="pt-6 border-t border-slate-200 flex flex-wrap gap-3">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => updateStatus(selected, s)}
                      data-testid={`detail-status-${s}`}
                      className={`border px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${
                        selected.status === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-slate-900"
                      }`}>{s}</button>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
